import React, { useState, useEffect } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Title,
  Button,
  Select,
  NumberInput,
  TextInput,
  Card,
  ActionIcon,
  Alert,
  Loader,
  Center,
  Divider
} from '@mantine/core';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconTrash,
  IconGripVertical,
  IconVideo,
  IconLivePhoto,
  IconClock,
  IconDeviceFloppy
} from '@tabler/icons-react';
import { curriculumService } from '../../services/curriculumService';
import { classService } from '../../services/classService';

const CurriculumBuilder = ({ curriculum, onSubmit, onCancel }) => {
  const [availableClasses, setAvailableClasses] = useState([]);
  const [currentClasses, setCurrentClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const addForm = useForm({
    initialValues: {
      class_id: '',
      schedule_time: '19:00',
      plus_days: 0,
    },
    validate: {
      class_id: (value) => (!value ? 'Please select a class' : null),
      schedule_time: (value) => {
        if (!value) return 'Schedule time is required';
        if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
          return 'Invalid time format (HH:MM)';
        }
        return null;
      },
      plus_days: (value) => (value < 0 ? 'Plus days cannot be negative' : null),
    },
  });

  useEffect(() => {
    loadData();
  }, [curriculum.id]);

  const loadData = async () => {
    try {
      const [classesData, curriculumData] = await Promise.all([
        classService.getClasses({ per_page: 1000 }),
        curriculumService.getCurriculum(curriculum.id)
      ]);
      
      setAvailableClasses(classesData.data);
      setCurrentClasses(curriculumData.curriculum_classes || []);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load data',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = (values) => {
    const selectedClass = availableClasses.find(c => c.id.toString() === values.class_id);
    if (!selectedClass) return;

    // Check if class already exists
    if (currentClasses.find(cc => cc.class.id.toString() === values.class_id)) {
      notifications.show({
        title: 'Warning',
        message: 'This class is already added to the curriculum',
        color: 'yellow',
      });
      return;
    }

    const newClass = {
      id: Date.now(), // Temporary ID for new items
      class_id: selectedClass.id,
      class: selectedClass,
      schedule_time: values.schedule_time,
      plus_days: values.plus_days,
      order_index: currentClasses.length,
    };

    setCurrentClasses([...currentClasses, newClass]);
    addForm.reset();
  };

  const handleRemoveClass = (index) => {
    const updatedClasses = currentClasses.filter((_, i) => i !== index);
    // Update order indexes
    const reorderedClasses = updatedClasses.map((item, i) => ({
      ...item,
      order_index: i
    }));
    setCurrentClasses(reorderedClasses);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(currentClasses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order indexes
    const reorderedClasses = items.map((item, index) => ({
      ...item,
      order_index: index
    }));

    setCurrentClasses(reorderedClasses);
  };

  const updateClassSchedule = (index, field, value) => {
    const updatedClasses = [...currentClasses];
    updatedClasses[index] = {
      ...updatedClasses[index],
      [field]: value
    };
    setCurrentClasses(updatedClasses);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const classesData = {
        classes: currentClasses.map((item, index) => ({
          class_id: item.class.id,
          schedule_time: item.schedule_time,
          plus_days: item.plus_days,
        }))
      };

      await curriculumService.addClasses(curriculum.id, classesData);
      
      notifications.show({
        title: 'Success',
        message: 'Curriculum updated successfully',
        color: 'green',
      });
      
      onSubmit();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update curriculum',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const getAvailableClassOptions = () => {
    const usedClassIds = currentClasses.map(cc => cc.class.id.toString());
    return availableClasses
      .filter(cls => !usedClassIds.includes(cls.id.toString()))
      .map(cls => ({
        value: cls.id.toString(),
        label: `${cls.title} (${cls.type === 'video_class' ? 'Video' : 'Live'})`
      }));
  };

  if (loading) {
    return (
      <Center h={300}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={4}>Building: {curriculum.title}</Title>
        <Group>
          <Button variant="light" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={saving}
          >
            Save Curriculum
          </Button>
        </Group>
      </Group>

      <Alert color="blue">
        <Text size="sm">
          Drag and drop to reorder classes. Students will access classes in this order, 
          based on the schedule time and plus days from batch start time.
        </Text>
      </Alert>

      {/* Add New Class */}
      <Paper withBorder p="md">
        <Stack>
          <Title order={5}>Add Class to Curriculum</Title>
          <form onSubmit={addForm.onSubmit(handleAddClass)}>
            <Group align="end">
              <Select
                label="Select Class"
                placeholder="Choose a class"
                data={getAvailableClassOptions()}
                style={{ flex: 1 }}
                {...addForm.getInputProps('class_id')}
              />
              <TextInput
                label="Schedule Time"
                placeholder="HH:MM"
                {...addForm.getInputProps('schedule_time')}
              />
              <NumberInput
                label="Plus Days"
                placeholder="0"
                min={0}
                {...addForm.getInputProps('plus_days')}
              />
              <Button
                type="submit"
                leftSection={<IconPlus size={16} />}
                disabled={getAvailableClassOptions().length === 0}
              >
                Add
              </Button>
            </Group>
          </form>
        </Stack>
      </Paper>

      <Divider label={`Current Classes (${currentClasses.length})`} />

      {/* Current Classes List */}
      {currentClasses.length === 0 ? (
        <Paper withBorder p="xl">
          <Center>
            <Stack align="center">
              <Text c="dimmed">No classes added yet</Text>
              <Text size="sm" c="dimmed">
                Add classes above to build your curriculum
              </Text>
            </Stack>
          </Center>
        </Paper>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="classes">
            {(provided) => (
              <div 
                {...provided.droppableProps} 
                ref={provided.innerRef}
                style={{ width: '100%', maxWidth: '100%' }}
              >
                <Stack>
                  {currentClasses.map((item, index) => (
                    <Draggable
                      key={`${item.class.id}-${index}`}
                      draggableId={`${item.class.id}-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          withBorder
                          p="md"
                          style={{
                            ...provided.draggableProps.style,
                            backgroundColor: snapshot.isDragging ? 'var(--mantine-color-blue-0)' : undefined,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}
                        >
                          <Group>
                            <div {...provided.dragHandleProps}>
                              <IconGripVertical size={20} color="gray" />
                            </div>
                            
                            <Stack gap="xs" style={{ flex: 1 }}>
                              <Group>
                                <Badge 
                                  color={item.class.type === 'video_class' ? 'blue' : 'green'}
                                  leftSection={
                                    item.class.type === 'video_class' ? 
                                      <IconVideo size={12} /> : 
                                      <IconLivePhoto size={12} />
                                  }
                                >
                                  Class {index + 1}
                                </Badge>
                                <Text fw={600}>{item.class.title}</Text>
                              </Group>
                              <Text size="sm" c="dimmed">
                                {item.class.short_description}
                              </Text>
                            </Stack>

                            <Group gap="xs">
                              <TextInput
                                size="xs"
                                placeholder="HH:MM"
                                value={item.schedule_time}
                                onChange={(e) => updateClassSchedule(index, 'schedule_time', e.target.value)}
                                leftSection={<IconClock size={14} />}
                                w={120}
                              />
                              <NumberInput
                                size="xs"
                                placeholder="Days"
                                value={item.plus_days}
                                onChange={(value) => updateClassSchedule(index, 'plus_days', value || 0)}
                                min={0}
                                w={80}
                              />
                              <Text size="xs" c="dimmed">
                                +{item.plus_days}d
                              </Text>
                            </Group>

                            <ActionIcon
                              color="red"
                              variant="light"
                              onClick={() => handleRemoveClass(index)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {currentClasses.length > 0 && (
        <Paper withBorder p="md">
          <Stack gap="xs">
            <Text fw={600} size="sm">Schedule Preview</Text>
            <Text size="xs" c="dimmed">
              Based on a sample batch starting tomorrow at 11:00 AM:
            </Text>
            {currentClasses.map((item, index) => {
              const sampleStart = new Date();
              sampleStart.setDate(sampleStart.getDate() + 1);
              sampleStart.setHours(11, 0, 0, 0);
              
              const availableDate = new Date(sampleStart);
              availableDate.setDate(availableDate.getDate() + item.plus_days);
              const [hours, minutes] = item.schedule_time.split(':');
              availableDate.setHours(parseInt(hours), parseInt(minutes));

              return (
                <Text key={index} size="xs" c="dimmed">
                  Class {index + 1}: Available {availableDate.toLocaleString('en-IN')}
                </Text>
              );
            })}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default CurriculumBuilder;